import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  checkPdf,
  saveDeliveryNote,
  removeDeliveryNote,
  discardPendingDeliveryNote,
  MAX_DELIVERY_NOTE_BYTES,
} from '../../services/deliveryNotes';
import type { DeliveryNote } from '../../types';

export type DeliveryNoteMessage = { kind: 'info' | 'error'; text: string } | null;

/**
 * Subir, sustituir y quitar el DDT de un contenedor (2026-10-23). Lo comparten
 * la sección de la ficha y el recuadro de opciones de la tarjeta, para que los
 * dos digan y hagan exactamente lo mismo.
 *
 * `onDone` recibe el DDT nuevo al subir, null al quitarlo y undefined cuando
 * la petición se quedó pendiente (sin conexión) o el servidor la rechazó.
 */
export function useDeliveryNoteActions(
  idCargoDetail: number | undefined,
  idCargo: number,
  onDone?: (note: DeliveryNote | null | undefined) => void,
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);
  const [message, setMessage] = useState<DeliveryNoteMessage>(null);

  const ready = typeof idCargoDetail === 'number' && idCargoDetail > 0;

  const pick = () => {
    setMessage(null);
    inputRef.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !ready) return;

    const check = await checkPdf(file);
    if (check !== 'ok') {
      setMessage({
        kind: 'error',
        text: check === 'tooBig'
          ? t('deliveryNote.tooBig', { max: MAX_DELIVERY_NOTE_BYTES / (1024 * 1024) })
          : t(check === 'empty' ? 'deliveryNote.empty' : 'deliveryNote.notPdf'),
      });
      return;
    }

    setBusy('upload');
    try {
      const res = await saveDeliveryNote(idCargoDetail!, idCargo, file, file.name || 'ddt.pdf');
      if (res.kind === 'done') {
        setMessage({ kind: 'info', text: t('deliveryNote.uploaded') });
        onDone?.(res.note ?? undefined);
      } else if (res.kind === 'queued') {
        setMessage({ kind: 'info', text: t('deliveryNote.queued') });
        onDone?.(undefined);
      } else {
        setMessage({ kind: 'error', text: t('deliveryNote.rejected', { reason: res.message }) });
        onDone?.(undefined);
      }
    } catch {
      setMessage({ kind: 'error', text: t('deliveryNote.failed') });
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!ready || !window.confirm(t('deliveryNote.confirmRemove'))) return;
    setMessage(null);
    setBusy('remove');
    try {
      const res = await removeDeliveryNote(idCargoDetail!, idCargo);
      if (res.kind === 'done') {
        setMessage({ kind: 'info', text: t('deliveryNote.removed') });
        onDone?.(null);
      } else if (res.kind === 'queued') {
        setMessage({ kind: 'info', text: t('deliveryNote.removeQueued') });
        onDone?.(undefined);
      } else {
        setMessage({ kind: 'error', text: t('deliveryNote.rejected', { reason: res.message }) });
        onDone?.(undefined);
      }
    } catch {
      setMessage({ kind: 'error', text: t('deliveryNote.failed') });
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    if (!ready) return;
    await discardPendingDeliveryNote(idCargoDetail!);
    setMessage(null);
    onDone?.(undefined);
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept="application/pdf,.pdf"
      style={{ display: 'none' }}
      onChange={onFile}
    />
  );

  return { ready, busy, message, setMessage, pick, remove, discard, fileInput };
}
